import { listen } from "@tauri-apps/api/event";
import { atom } from "jotai";

import { Logger } from "@moment/logging";

import { env } from "../env/env";
import { type FileChangedPayload, FileChangedPayloadSchema } from "./file-watcher.types";

const logger = new Logger("file-watcher.atom");

export const FILE_CHANGED_EVENT = "file-watcher:file-changed";

let unlistenPromise: Promise<() => void> | null = null;
let setFileChanges:
	| ((updater: (prev: Record<string, string>) => Record<string, string>) => void)
	| null = null;

function startListening() {
	if (unlistenPromise || !env.isDesktopApp) {
		return;
	}

	logger.info("Starting file watcher listener");

	unlistenPromise = listen<FileChangedPayload>(FILE_CHANGED_EVENT, (event) => {
		const result = FileChangedPayloadSchema.safeParse(event.payload);
		if (!result.success) {
			logger.warn("Invalid file changed event payload", { payload: event.payload });
			return;
		}

		logger.debug("File changed event received", { path: result.data.path });

		// IMPORTANT: Why not just pass `set` in as an argument? Because this deals with
		// late-arriving events. If we unsubscribe, this set function will instantly be `null`, and
		// late events won't be called on it. So there is no opportunity for a race here.
		if (setFileChanges) {
			setFileChanges((prev) => ({
				...prev,
				[result.data.path]: result.data.content,
			}));
		} else {
			logger.warn("File changed but setFileChanges is null - atom not mounted?", {
				path: result.data.path,
			});
		}
	});
}

function stopListening() {
	if (!unlistenPromise) {
		return;
	}

	logger.info("Stopping file watcher listener");

	void unlistenPromise.then((unlisten) => unlisten());
	unlistenPromise = null;
}

export const $fileChanges = atom<Record<string, string>>({});
$fileChanges.debugLabel = "fileChanges";

$fileChanges.onMount = (set) => {
	logger.info("$fileChanges atom mounted", { isDesktopApp: env.isDesktopApp });

	if (!env.isDesktopApp) {
		return;
	}

	setFileChanges = set;
	startListening();

	return () => {
		logger.info("$fileChanges atom unmounting");
		setFileChanges = null;
		stopListening();
	};
};
